import { getPxValue } from "../hooks/useResize";

/**
 * UI Constants for consistent styling across components
 *
 * This file centralizes magic numbers and hardcoded values to improve
 * maintainability and ensure consistency across the application.
 */
export const UI_CONSTANTS = {
  // Button sizes
  BUTTONS: {
    MEDIUM_SIZE: 44,
    SMALL_HEIGHT: 28
  },

  // Typography
  TYPOGRAPHY: {
    SMALL_FONT: "0.7rem",
    CAPTION_FONT: "0.65rem",
    FONT_WEIGHT_MEDIUM: 500
  },

  // Spacing
  SPACING: {
    RIGHT_GAP: "20px",
    PANEL_PADDING: 2,
    BORDER_RADIUS: 4,
    SMALL_GAP: 1,
    LARGE_GAP: 3
  },

  // Shadows (matching CMChip)
  SHADOWS: {
    DEFAULT: "0 2px 4px rgba(0, 0, 0, 0.1)",
    HOVER: "0 3px 6px rgba(0, 0, 0, 0.1)",
    ENHANCED: "0 8px 32px rgba(0, 0, 0, 0.1)",
    GLASS_DARK: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
    GLASS_LIGHT: "0 8px 32px 0 rgba(31, 38, 135, 0.37)"
  },

  // Status bar
  STATUS_BAR: {
    MIN_HEIGHT_SMALL: "40px",
    MIN_HEIGHT_LARGE: "48px"
  },

  // Layout dimensions
  LAYOUT: {
    THUMBNAIL_SIZE: 60,
    CARD_HEIGHT_COMPACT: 158,
    CARD_HEIGHT_FULL: 158,
    MIN_ITEM_WIDTH_COMPACT: 100,
    MIN_ITEM_WIDTH_FULL: 153,
    TABLE_MIN_WIDTH: 800,
    TABLE_CELL_MIN_WIDTH: "150px",
    SCROLL_THRESHOLD: 200,
    INDICATOR_SIZE: 8
  },

  // Z-indexes
  Z_INDEX: {
    ENDPOINT_BUTTONS: 2000000,
    LOADING_OVERLAY: 1200,
    TOGGLE_BUTTON: 10
  },

  // Colors and opacity
  COLORS: {
    GLASS_BACKGROUND_OPACITY: 0.1,
    BACKDROP_BLUR: "blur(10px)",
    SUBTLE_BORDER: "rgba(255, 255, 255, 0.1)",
    SUBTLE_BACKGROUND: "rgba(255, 255, 255, 0.1)",
    WHITE_BORDER: "1px solid white",
    BLACK_BORDER: "1px solid black"
  },

  // Card-specific constants
  CARDS: {
    COMPACT_WIDTH: 100,
    BORDER_RADIUS_LARGE: "8px",
    BACKDROP_BLUR_LIGHT: "blur(4px)",
    BACKDROP_BLUR_HEAVY: "blur(8px)",
    LIFT_TRANSFORM: "translateY(-2px)",
    SUBTLE_LIFT: "translateY(-1px)",
    OVERLAY_Z_INDEX: 10,
    TEXT_Z_INDEX: 1
  }
} as const;

// Type for accessing constants with intellisense
export type UIConstants = typeof UI_CONSTANTS;

/**
 * Sidebar Resizing Constraints
 *
 * We enforce minimum and maximum widths to guarantee a good user experience:
 *
 * 1. MIN_DATA_DRAWER_WIDTH ("18%"):
 *    Prevents the sidebar from becoming too narrow to display content effectively,
 *    ensuring text remains readable and controls are accessible.
 *    Default width is also set to similar width but on numeric value
 *
 * 2. MAX_DATA_DRAWER_WIDTH ("75%"):
 *    Restricts the sidebar from expanding too far and obscuring the main map view.
 *    This ensures that map popups, selection highlights, and other
 *    elements remain visible and are interactive.
 */
export const DEFAULT_SIDEBAR_WIDTH_IN_PX = getPxValue("18%"); // default value of drawer in px (it is on numeric to enable calculation if needed)
export const MIN_DATA_DRAWER_WIDTH = "18%";
export const MAX_DATA_DRAWER_WIDTH = "75%";
/**
 * Data drawer height if multi-source data is enabled
 * use this when you have multi-source data type is enabled and directly under the
 * api-endpoint button component
 **/
export const DATA_DRAWER_MULTI_SOURCE_HEIGHT = "calc(100dvh - 286.4px)";
export const DATA_DRAWER_HEIGHT = "calc(100dvh - 240px)";
export const SIDEBAR_TOGGLE_ICON_OFFSET = -33;
export const RESIZING_ICON_OFFSET = -33;
export const RESIZING_ICON_TOP = "12%";
